import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundaryContainer, PageContainer } from './MainRoutes.styles';
import { LoadingSpinner, useTheme } from '@tw/tw-component-library';
import { useRouteTransition } from '../../../hooks/useRouteTransition';
import { isValidRoute } from '../../../utils/routeUtils';
import { CentralizedWorkflowRouter } from '../CentralisedWorkflow';

// Static components (Dashboard, People, etc.)
const Dashboard = React.lazy(() =>
  import('../../pages/Dashboard').then((m) => ({ default: m.Dashboard }))
);
const PeoplePage = React.lazy(() =>
  import('../../pages/static/People').then((m) => ({ default: m.PeoplePage }))
);
const FilesPage = React.lazy(() =>
  import('../../pages/static/Files').then((m) => ({ default: m.FilesPage }))
);
const ForbiddenPage = React.lazy(() =>
  import('../../pages/static/Forbidden').then((m) => ({
    default: m.ForbiddenPage,
  }))
);
const ActivitiesPage = React.lazy(() =>
  import('../../pages/static/Activities').then((m) => ({
    default: m.ActivitiesPage,
  }))
);

// Note: All workflows now use the centralized system
// Individual workflow components are no longer needed

// Base static routes
const baseRouteConfig = [
  { path: '/', component: Dashboard },
  { path: '/people', component: PeoplePage },
];

// Error handler component
const FunctionalErrorHandler: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { theme } = useTheme();
  const [errorState, setErrorState] = React.useState<{
    hasError: boolean;
    error: Error | null;
    errorInfo: { componentStack: string } | null;
  }>({
    hasError: false,
    error: null,
    errorInfo: null,
  });

  React.useEffect(() => {
    const handleWindowError = (event: ErrorEvent) => {
      setErrorState({
        hasError: true,
        error: event.error,
        errorInfo: { componentStack: event.error?.stack || '' },
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      setErrorState({
        hasError: true,
        error: new Error(event.reason?.message || 'Promise rejection'),
        errorInfo: { componentStack: event.reason?.stack || 'No stack trace' },
      });
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, []);

  // Reset error state when children change (route change)
  React.useEffect(() => {
    setErrorState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  }, [children]);

  if (errorState.hasError) {
    return (
      <ErrorBoundaryContainer>
        <h3>Something went wrong</h3>
        <p>Please try refreshing the page.</p>
        <details style={{ whiteSpace: 'pre-wrap', marginTop: 20 }}>
          <summary
            style={{ cursor: 'pointer', color: theme.colors.primary }}
          >
            View Error Details
          </summary>
          <p style={{ color: theme.colors.error, fontFamily: 'monospace' }}>
            {errorState.error?.toString()}
          </p>
          {errorState.errorInfo && (
            <div
              style={{
                fontSize: 12,
                color: theme.colors.text.secondary,
                marginTop: 10,
              }}
            >
              Component Stack:
              <pre
                style={{
                  color: theme.colors.text.primary,
                  marginTop: 5,
                  overflowX: 'auto',
                }}
              >
                {errorState.errorInfo.componentStack}
              </pre>
            </div>
          )}
        </details>
      </ErrorBoundaryContainer>
    );
  }

  return <>{children}</>;
};

export const MainRoutes: React.FC = () => {
  const { isLoading, error } = useRouteTransition();

  // All workflows now use the centralized routing system

  if (error) {
    return (
      <ErrorBoundaryContainer>
        <h3>Error Loading Page</h3>
        <p>{error.message}</p>
      </ErrorBoundaryContainer>
    );
  }

  return (
    <FunctionalErrorHandler>
      <PageContainer $isTransitioning={isLoading}>
        <Suspense fallback={<LoadingSpinner variant="fullPage" />}>
          <Routes>
            {/* Static routes */}
            {baseRouteConfig.map(({ path, component: Component }) => (
              <Route
                key={path}
                path={path}
                element={
                  isValidRoute(path) ? (
                    <Component />
                  ) : (
                    <Navigate to="/404" replace />
                  )
                }
              />
            ))}

            {/* All workflows now use centralized routing */}

            {/* New centralized workflow routes */}
            <Route
              path="/workflows/*"
              element={<CentralizedWorkflowRouter />}
            />
            <Route path="/request/*" element={<CentralizedWorkflowRouter />} />

            {/* Static detail routes with workflowType */}
            <Route path="/files/:workflowType/:id" element={<FilesPage />} />
            <Route
              path="/activities/:workflowType/:id"
              element={<ActivitiesPage />}
            />
            <Route path="/403" element={<ForbiddenPage />} />

            {/* 404 fallback */}
            <Route
              path="*"
              element={
                <ErrorBoundaryContainer>
                  <h3>Page Not Found</h3>
                  <p>The page you're looking for doesn't exist.</p>
                </ErrorBoundaryContainer>
              }
            />
          </Routes>
        </Suspense>
      </PageContainer>
    </FunctionalErrorHandler>
  );
};
